import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { ethers } from 'ethers';
import { signer, createContract, getAccounts, initializeEthers } from './EthersSetup';
import walletManager from '../wallet/WalletManager.js';
import RiskSimple from '../deployedContractsInfo/RiskSimple.json';
import GameMakerConfig from '../deployedContractsInfo/GameMaker.json';
import contractAddresses from '../deployedContractsInfo/contract-address.json';
import { loadMapData, getMapDisplayName } from '../maps_generated/risk/map-registry.js';
import useRiskHistory from '../hooks/useRiskHistory';
import blockchainDataManager from '../services/BlockchainDataManager.js';
import AddressDisplay, { AddressDisplayLong } from './AddressDisplay.js';
import { TransactionLink } from './LinkDisplay';
import GameTransactionHistory from './basics/GameTransactionHistory';
import RiskHistoryPanel from './RiskHistoryPanel';
import RiskCombatDetails from './RiskCombatDetails';
import TopRightBar from './basics/TopRightBar';
import { FullGameSkeleton, TransactionPanelSkeleton } from './ui/SkeletonLoader';

const RiskGame = () => {
  const { gameID = '1' } = useParams();
  const gameIDNumber = parseInt(gameID); // Convert to number for contract calls
  const location = useLocation();
  
  // Parse map parameter from URL
  const urlParams = new URLSearchParams(location.search);
  const mapParam = urlParams.get('map') || 'simple';
  
  // Map contract names, addresses, and ABIs
  const getContractInfo = (mapType) => {
    const contractMap = {
      'simple': { address: contractAddresses.RiskSimple, name: 'RiskSimple', abi: RiskSimple.abi }
    };
    return contractMap[mapType] || contractMap['simple'];
  };
  
  const contractInfo = getContractInfo(mapParam);
  
  const [gameState, setGameState] = useState({
    player1: '',
    player2: '',
    status: '',
    gameFinished: false,
    lastMoveTime: 0,
    contribution1: 0,
    contribution2: 0,
    currentPlayer: 1,
    territoryOwners: new Array(50).fill(0), // Will be resized when map loads
    territoryUnits: new Array(50).fill(0), // Will be resized when map loads
    mapType: mapParam // Use map parameter from URL
  });

  const [mapData, setMapData] = useState(null);

  // Contract state management for async creation
  const [readOnlyContract, setReadOnlyContract] = useState(null);
  const [gameMakerContract, setGameMakerContract] = useState(null);

  // Initialize read-only contract asynchronously
  useEffect(() => {
    const initReadOnlyContract = async () => {
      try {
        const contract = await createContract(contractInfo.address, contractInfo.abi, false);
        setReadOnlyContract(contract);
      } catch (error) {
        console.warn('[Risk] Failed to create read-only contract:', error);
        setReadOnlyContract(null);
      }
    };
    if (contractInfo.address && contractInfo.abi) {
      initReadOnlyContract();
    }
  }, [contractInfo.address, contractInfo.abi]);

  // Create GameMaker contract for transaction history
  useEffect(() => {
    const initGameMakerContract = async () => {
      try {
        const contract = await createContract(contractAddresses.GameMaker, GameMakerConfig.abi);
        setGameMakerContract(contract);
      } catch (error) {
        console.error('Risk: Failed to create GameMaker contract:', error);
      }
    };
    initGameMakerContract();
  }, []);

  const [userState, setUserState] = useState({ userAddress: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [uiReady, setUiReady] = useState(false);
  const [loadingStages, setLoadingStages] = useState({
    mapData: true,       // Map SVG and territory data
    gameBasics: true,    // Player names, status
    gameDetails: true,   // Territory owners, units
    transactions: true,  // Transaction history
    userFeatures: true   // Wallet-dependent features
  });
  const [isWalletInitialized, setIsWalletInitialized] = useState(false);
  const [rulesCollapsed, setRulesCollapsed] = useState(true);

  // Memoized game events for GameTransactionHistory to prevent constant reloading
  const riskGameEvents = useMemo(() => [
    { name: 'TurnSubmitted', label: 'Turn Submitted' },
    { name: 'Combat', label: 'Combat Resolution' },
    { name: 'TerritoryConquered', label: 'Territory Conquered' }
  ], []);
  const [selectedMoves, setSelectedMoves] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [moveInProgress, setMoveInProgress] = useState(false);
  const [customUnitInput, setCustomUnitInput] = useState('');
  const [transactionPending, setTransactionPending] = useState(null); // { type: 'submit', numMoves: number, message: string }

  // History state management
  const [riskContract, setRiskContract] = useState(null);
  const [signerContract, setSignerContract] = useState(null);
  const [provider, setProvider] = useState(null);
  const [displayState, setDisplayState] = useState(null); // State being displayed (current or historical)
  const [isViewingHistory, setIsViewingHistory] = useState(false);

  // Map zoom and pan state
  const [mapTransform, setMapTransform] = useState({
    zoom: 1,
    panX: 0,
    panY: 0
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const svgRef = useRef(null);

  // Initialize history hook
  const historyHook = useRiskHistory(riskContract, gameIDNumber);


  // Helper function to safely convert BigNumber to number
  const safeToNumber = (value) => {
    if (value && typeof value.toNumber === 'function') {
      return value.toNumber();
    }
    return Number(value);
  };

  // Staggered loading functions for different data stages
  const loadMapDataAsync = useCallback(async () => {
    try {
      const mapDataResult = await loadMapData(mapParam);
      setMapData(mapDataResult);

      // Resize territory arrays based on map data
      const territoryCount = mapDataResult.territories.length;
      setGameState(prev => ({
        ...prev,
        territoryOwners: new Array(territoryCount).fill(0),
        territoryUnits: new Array(territoryCount).fill(0)
      }));

      setLoadingStages(prev => ({ ...prev, mapData: false }));
      console.log('[Risk] Map data loaded:', mapParam);
    } catch (error) {
      console.error('[Risk] Failed to load map data:', error);
      setErrorMessage('Failed to load map data');
      setLoadingStages(prev => ({ ...prev, mapData: false }));
    }
  }, [mapParam]);

  const loadGameBasics = useCallback(async () => {
    if (!readOnlyContract) {
      console.log('[Risk] Waiting for contract to be ready...');
      return;
    }

    try {
      const gameState = await readOnlyContract.getGameState(gameIDNumber);
      const basicGameState = {
        player1: gameState.player1,
        player2: gameState.player2,
        status: gameState.status.toString(),
        gameFinished: !gameState.gameActive,
        lastMoveTime: Number(gameState.lastMoveTime),
        contribution1: Number(gameState.contribution1),
        contribution2: Number(gameState.contribution2),
        currentPlayer: Number(gameState.currentPlayer)
      };

      setGameState(prev => ({ ...prev, ...basicGameState }));
      setLoadingStages(prev => ({ ...prev, gameBasics: false }));
      console.log('[Risk] Game basics loaded');
    } catch (error) {
      console.error('[Risk] Failed to load game basics:', error);
      setErrorMessage('Failed to load game information');
      setLoadingStages(prev => ({ ...prev, gameBasics: false }));
    }
  }, [gameIDNumber, readOnlyContract, contractInfo]);

  const loadGameDetails = useCallback(async () => {
    if (!readOnlyContract) {
      console.log('[Risk] Waiting for contract to be ready...');
      return;
    }

    try {

      // Load territory owners and units in parallel for better performance
      const territoryCount = mapData ? mapData.territories.length : 50;

      // Create batched promises for all territories
      const ownerPromises = [];
      const unitPromises = [];

      for (let i = 0; i < territoryCount; i++) {
        ownerPromises.push(
          readOnlyContract.getTerritoryOwner(gameIDNumber, i)
            .then(owner => Number(owner))
            .catch(() => 0) // Default to 0 on error, no logging
        );
        unitPromises.push(
          readOnlyContract.getTerritoryUnits(gameIDNumber, i)
            .then(units => Number(units))
            .catch(() => 0) // Default to 0 on error, no logging
        );
      }

      // Execute all promises in parallel
      const [territoryOwners, territoryUnits] = await Promise.all([
        Promise.all(ownerPromises),
        Promise.all(unitPromises)
      ]);

      setGameState(prev => ({ ...prev, territoryOwners, territoryUnits }));
      setLoadingStages(prev => ({ ...prev, gameDetails: false }));
      console.log('[Risk] Game details loaded');
    } catch (error) {
      console.error('[Risk] Failed to load game details:', error);
      setLoadingStages(prev => ({ ...prev, gameDetails: false }));
    }
  }, [gameIDNumber, readOnlyContract, contractInfo, mapData]);


  const initializeWalletFeatures = useCallback(async () => {
    try {
      if (!userState.userAddress) {
        setLoadingStages(prev => ({ ...prev, userFeatures: false }));
        return;
      }

      // Create signer contract when wallet is available
      const signerContractInstance = await createContract(contractInfo.address, contractInfo.abi, true);
      setSignerContract(signerContractInstance);

      setLoadingStages(prev => ({ ...prev, userFeatures: false }));
      console.log('[Risk] Wallet features initialized');
    } catch (error) {
      console.error('[Risk] Failed to initialize wallet features:', error);
      setLoadingStages(prev => ({ ...prev, userFeatures: false }));
    }
  }, [userState.userAddress, contractInfo]);

  // Main initialization with staggered loading
  useEffect(() => {
    // Show UI immediately
    setUiReady(true);

    // Start loading map data and game basics immediately (non-blocking)
    loadMapDataAsync();
    loadGameBasics();

    // Initialize wallet in background
    const initWallet = async () => {
      try {
        await initializeEthers();
        const accounts = await getAccounts();
        if (accounts.length > 0) {
          setUserState({ userAddress: accounts[0] });
        }
        setIsWalletInitialized(true);
      } catch (error) {
        console.error('Risk: Failed to initialize wallet:', error);
        setIsWalletInitialized(true); // Still allow component to continue
      }
    };
    initWallet();
  }, [loadMapDataAsync, loadGameBasics]);

  // Load game details after map data and basics are loaded
  useEffect(() => {
    if (!loadingStages.mapData && !loadingStages.gameBasics && loadingStages.gameDetails) {
      loadGameDetails();
    }
  }, [loadingStages.mapData, loadingStages.gameBasics, loadingStages.gameDetails, loadGameDetails]);

  // Initialize wallet features when wallet is ready
  useEffect(() => {
    if (isWalletInitialized) {
      initializeWalletFeatures();
    }
  }, [isWalletInitialized, initializeWalletFeatures]);

  // Load transaction history when panel is expanded

  // Set browser tab title for the game
  useEffect(() => {
    const originalTitle = document.title;
    document.title = `Risk #${gameID}`;

    // Cleanup: restore original title when component unmounts
    return () => {
      document.title = originalTitle;
    };
  }, [gameID]);

  // Auto-pause polling when game finishes
  useEffect(() => {
    const isGameFinished = gameState.gameFinished === true;
    window.dispatchEvent(new CustomEvent('setPollingPause', {
      detail: {
        isPaused: isGameFinished,
        reason: isGameFinished ? 'game-finished' : ''
      }
    }));
  }, [gameState.gameFinished]);

  // Set up provider and risk contract when game details are loaded
  useEffect(() => {
    if (!loadingStages.gameDetails && !loadingStages.userFeatures) {
      const currentProvider = walletManager.getProvider();
      setProvider(currentProvider);

      // Use the signer contract as the main risk contract
      if (signerContract) {
        setRiskContract(signerContract);
      }
    }
  }, [loadingStages.gameDetails, loadingStages.userFeatures, signerContract]);

  // Event handler for TurnSubmitted events - preserves original logic
  const handleTurnSubmitted = useCallback(async (gameId, player, numMoves, event) => {
    // Only refresh if this event is for our game
    if (gameId.toString() === gameIDNumber.toString()) {
      console.log('Turn submitted by', player, 'with', numMoves.toString(), 'moves');
      
      // Check if we are a player in this game and it's not our turn
      try {
        const accounts = await getAccounts();
        const currentUserAddress = accounts[0]?.toLowerCase();
        const eventPlayerAddress = player.toLowerCase();
        
        console.log('🔍 Event handler debugging:', {
          currentUserAddress,
          eventPlayerAddress,
          isOtherPlayer: currentUserAddress !== eventPlayerAddress
        });
        
        // Get current game state to check player addresses
        const currentGameState = await riskContract.gameInstances(gameIDNumber);
        
        const player1Address = currentGameState.player1.toLowerCase();
        const player2Address = currentGameState.player2.toLowerCase();
        
        console.log('🔍 Game players:', {
          player1Address,
          player2Address,
          userIsPlayer1: currentUserAddress === player1Address,
          userIsPlayer2: currentUserAddress === player2Address
        });
        
        const shouldRefresh = currentUserAddress !== eventPlayerAddress &&
                             (currentUserAddress === player1Address || currentUserAddress === player2Address);

        // Clear transaction pending if this event is from the current user's turn submission
        const isCurrentUserTurn = currentUserAddress === eventPlayerAddress;
        if (isCurrentUserTurn && transactionPending) {
          console.log('🔄 Clearing transaction pending - current user turn confirmed');
          setTransactionPending(null);
        }

        console.log('🔍 Refresh decision:', { shouldRefresh, isCurrentUserTurn });
        
        // If the event is from the other player, refresh our game state
        if (shouldRefresh) {
          
          // Small delay to ensure blockchain state is updated
          setTimeout(async () => {
            console.log('🔄 Starting game state refresh after 1 second delay...');
            try {
              // Reload the full game state from public mapping (contains all territory info)
              const gameInstance = await riskContract.gameInstances(gameIDNumber);
              const player1 = gameInstance.player1;
              const player2 = gameInstance.player2;
              const status = gameInstance.status.toString();
              const gameFinished = !gameInstance.gameActive;
              const lastMoveTime = safeToNumber(gameInstance.lastMoveTime);
              const contribution1 = gameInstance.contribution1;
              const contribution2 = gameInstance.contribution2;
              const currentPlayer = safeToNumber(gameInstance.currentPlayer);

              // Load territory data with bulk API call (same as turn submission)
              // Use same territory array size as current game state
              let territoryOwners = new Array(gameState.territoryOwners.length).fill(0);
              let territoryUnits = new Array(gameState.territoryUnits.length).fill(0);
              
              try {
                const territoryInfo = await riskContract.getTerritoryInfo(gameIDNumber);
                territoryOwners = territoryInfo.owners.map(owner => safeToNumber(owner));
                territoryUnits = territoryInfo.units.map(units => safeToNumber(units));
                console.log('Territory data reloaded after enemy turn');
              } catch (error) {
                console.log('Event refresh: Game exists but territories not initialized yet.');
                console.error('Error reloading territory data during event refresh:', error);
                // Keep default empty arrays
              }

              const newGameState = { 
                player1, 
                player2, 
                status, 
                gameFinished, 
                lastMoveTime, 
                contribution1, 
                contribution2,
                currentPlayer,
                territoryOwners,
                territoryUnits,
                mapType: mapParam
              };
              
              console.log('🎯 Game state refreshed after turn submission, updating UI...', {
                currentPlayer: newGameState.currentPlayer,
                status: newGameState.status,
                territoryCount: newGameState.territoryOwners.filter(owner => owner > 0).length
              });
              
              setGameState(prevState => ({
                ...prevState,
                ...newGameState
              }));
              
              if (!isViewingHistory) {
                setDisplayState(prevState => ({
                  ...prevState,
                  ...newGameState
                }));
              }
              
              // Refresh history if available
              if (historyHook?.refreshHistory) {
                await historyHook.refreshHistory();
              }
              
              // Transaction history will be updated by GameTransactionHistory component
            } catch (error) {
              console.error('Failed to refresh game state after turn submission:', error);
            }
          }, 1000);
        }
      } catch (error) {
        console.error('Error handling turn submitted event:', error);
      }
    }
  }, [riskContract, gameIDNumber, isViewingHistory, mapParam, historyHook]);

  // Set up polling system for Risk page (2 contracts: GameMaker + RiskClassic)
  useEffect(() => {
    if (!provider || !riskContract || isViewingHistory) return;

    // Configure polling for this game page - ensure full setup
    const setupPolling = async () => {
      try {
        // Initialize the complete polling system
        await blockchainDataManager.setupSmartPolling();

        // Add RiskClassic to the polled contracts (GameMaker is already included)
        blockchainDataManager.addToPolledContracts('RiskClassic');

        console.log('[Risk] Polling system fully initialized with 2 contracts');
      } catch (error) {
        console.error('[Risk] Failed to setup polling system:', error);
      }
    };

    setupPolling();

    // Subscribe to game updates from polling system
    const handleGameInstanceUpdate = (data) => {
      // Filter updates for this specific game
      if (data.source === 'polling' && (data.contract === 'RiskClassic' || data.contract === 'GameMaker')) {
        console.log(`[Risk] Polling update from ${data.contract}, refreshing game state...`);
        handleTurnSubmitted();
      }
    };

    const unsubscribe = blockchainDataManager.subscribe('gameInstancesUpdated', handleGameInstanceUpdate);

    return unsubscribe;
  }, [provider, riskContract, gameIDNumber, isViewingHistory, handleTurnSubmitted]);

  // Log polling system status for debugging
  useEffect(() => {
    console.log(`[Risk] Polling system configured for contracts: GameMaker, RiskClassic`);
  }, [gameIDNumber]);

  // Synchronize local state with history hook
  useEffect(() => {
    if (historyHook) {
      setIsViewingHistory(historyHook.isViewingHistory);

      // Update display state when viewing history changes
      if (historyHook.isViewingHistory) {
        // Get the historical state from the current turn
        historyHook.getMapState().then(historicalState => {
          if (historicalState) {
            console.log('History: Setting displayState for turn', historyHook.currentTurnIndex, 'owners sample:', historicalState.territoryOwners?.slice(0, 5));
            setDisplayState(historicalState);
          }
        }).catch(error => {
          console.error('Error loading historical state:', error);
        });
      } else {
        // When returning to live state, use current game state
        console.log('History: Returning to live state, owners sample:', gameState.territoryOwners?.slice(0, 5));
        setDisplayState(gameState);
      }
    }
  }, [historyHook, historyHook?.currentTurnIndex, historyHook?.isViewingHistory, gameState]);


  const getStatusMessage = (status) => {
    switch (status) {
      case '0': return 'Player 1 Turn';
      case '1': return 'Player 2 Turn';
      case '2': return 'Game Ended - Draw';
      case '3': return 'Game Ended - Player 1 Won';
      case '4': return 'Game Ended - Player 2 Won';
      case '5': return 'Game Ended - Killed';
      case '6': return 'Both Players Turn';
      default: return 'Unknown Status';
    }
  };

  const formatAddress = (address, truncate = false) => {
    if (!truncate) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatEth = (wei) => {
    return ethers.utils.formatEther(wei.toString());
  };


  const getUserRole = () => {
    const isSamePlayer = gameState.player1.toLowerCase() === gameState.player2.toLowerCase();
    const isPlayer1 = userState.userAddress.toLowerCase() === gameState.player1.toLowerCase();
    const isPlayer2 = userState.userAddress.toLowerCase() === gameState.player2.toLowerCase();
    
    if (isSamePlayer && (isPlayer1 || isPlayer2)) {
      return 'Both Players';
    } else if (isPlayer1) {
      return 'Player 1';
    } else if (isPlayer2) {
      return 'Player 2';
    }
    return 'Spectator';
  };

  const getWinnerMessage = () => {
    if (!gameState.gameFinished) return '';

    if (gameState.status === '3') {
      return 'Player 1 controls all territories!';
    } else if (gameState.status === '4') {
      return 'Player 2 controls all territories!';
    } else if (gameState.status === '5') {
      return 'Game was terminated via killswitch';
    } else if (gameState.status === '2') {
      return 'Game ended in a draw';
    }
    return '';
  };



  // Use dynamic isValidTarget function with selectedSource parameter
  const isValidTargetForSource = (targetIndex) => {
    if (selectedSource === null || !mapData) return false;
    if (targetIndex === selectedSource) return false;
    return mapData.isValidTarget(selectedSource, targetIndex);
  };

  const getAvailableUnits = (territoryIndex) => {
    // Use current viewing state
    const currentViewState = displayState || gameState;
    // Get base units for this territory
    let baseUnits = currentViewState.territoryUnits[territoryIndex];
    
    // Subtract units already moved from this territory in planned moves
    const movesFromThisTerritory = selectedMoves.filter(move => move.fromTerritory === territoryIndex);
    const unitsAlreadyMoved = movesFromThisTerritory.reduce((sum, move) => sum + move.unitCount, 0);
    
    // Must leave at least 1 unit
    return Math.max(0, baseUnits - unitsAlreadyMoved - 1);
  };

  const getDisplayUnits = (territoryIndex) => {
    // Use current viewing state
    const currentViewState = displayState || gameState;
    // Get base units for this territory
    let baseUnits = currentViewState.territoryUnits[territoryIndex];
    const currentOwner = currentViewState.territoryOwners[territoryIndex];
    
    // Subtract units moved from this territory
    const movesFromThisTerritory = selectedMoves.filter(move => move.fromTerritory === territoryIndex);
    const unitsMovedOut = movesFromThisTerritory.reduce((sum, move) => sum + move.unitCount, 0);
    
    // Add units moved to this territory (only for friendly transfers, not attacks)
    const movesToThisTerritory = selectedMoves.filter(move => {
      // Only count moves to territories we own as reinforcements
      return move.toTerritory === territoryIndex && currentOwner === currentViewState.currentPlayer;
    });
    const unitsMovedIn = movesToThisTerritory.reduce((sum, move) => sum + move.unitCount, 0);
    
    return baseUnits - unitsMovedOut + unitsMovedIn;
  };


  const getUnitSelectionButtons = () => {
    if (selectedSource === null || selectedTarget === null) return [];
    
    const availableUnits = getAvailableUnits(selectedSource);
    if (availableUnits <= 0) return [];
    
    // Always show max 4 predefined options
    const buttons = [];
    if (availableUnits >= 1) buttons.push(1);
    if (availableUnits >= 3) buttons.push(3);
    if (availableUnits > 3) {
      const half = Math.ceil(availableUnits / 2);
      if (half > 3 && half < availableUnits) buttons.push(half);
    }
    if (availableUnits > 1) buttons.push(availableUnits); // max
    
    return buttons.slice(0, 4); // Ensure max 4 buttons
  };

  // Zoom and pan functions
  const handleZoom = (delta, centerX = (mapData?.MAP_SETTINGS?.mapWidth || 320) / 2, centerY = (mapData?.MAP_SETTINGS?.mapHeight || 290) / 2) => {
    const zoomFactor = delta > 0 ? 1.2 : 0.8;
    const newZoom = Math.max(0.5, Math.min(5, mapTransform.zoom * zoomFactor));
    
    if (newZoom !== mapTransform.zoom) {
      // Calculate pan adjustment to zoom toward center point
      const panAdjustX = (centerX - mapTransform.panX) * (1 - zoomFactor);
      const panAdjustY = (centerY - mapTransform.panY) * (1 - zoomFactor);
      
      setMapTransform(prev => ({
        zoom: newZoom,
        panX: prev.panX + panAdjustX,
        panY: prev.panY + panAdjustY
      }));
    }
  };

  const handleMouseDown = (e) => {
    if (e.button === 0) { // Left mouse button
      setIsDragging(true);
      setDragStart({
        x: e.clientX - mapTransform.panX,
        y: e.clientY - mapTransform.panY
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setMapTransform(prev => ({
        ...prev,
        panX: e.clientX - dragStart.x,
        panY: e.clientY - dragStart.y
      }));
      e.preventDefault();
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = () => {
    // Remove wheel zoom functionality - use buttons only
    // This prevents conflicts with page scrolling
  };

  const resetView = () => {
    setMapTransform({ zoom: 1, panX: 0, panY: 0 });
  };

  const zoomIn = () => {
    handleZoom(1);
  };

  const zoomOut = () => {
    handleZoom(-1);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      // Single touch - prepare for potential panning, but don't set dragging yet
      const touch = e.touches[0];
      setDragStart({
        x: touch.clientX - mapTransform.panX,
        y: touch.clientY - mapTransform.panY,
        startX: touch.clientX,
        startY: touch.clientY,
        hasMoved: false
      });
      e.preventDefault();
    } else if (e.touches.length === 2) {
      // Two finger touch - prepare for pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) + 
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      setIsDragging(true); // Two-finger gestures always count as dragging
      setDragStart({ x: 0, y: 0, distance, zoom: mapTransform.zoom });
      e.preventDefault();
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      
      // Check if touch has moved significantly (threshold for detecting drag vs tap)
      if (!isDragging && dragStart.startX !== undefined) {
        const deltaX = Math.abs(touch.clientX - dragStart.startX);
        const deltaY = Math.abs(touch.clientY - dragStart.startY);
        const moveDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        
        if (moveDistance > 10) { // 10px threshold
          setIsDragging(true);
        }
      }
      
      // If dragging, update pan position
      if (isDragging) {
        setMapTransform(prev => ({
          ...prev,
          panX: touch.clientX - dragStart.x,
          panY: touch.clientY - dragStart.y
        }));
        e.preventDefault();
      }
    } else if (e.touches.length === 2) {
      // Two finger touch - pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) + 
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      
      if (dragStart.distance) {
        const scale = distance / dragStart.distance;
        const newZoom = Math.max(0.5, Math.min(5, dragStart.zoom * scale));
        setMapTransform(prev => ({
          ...prev,
          zoom: newZoom
        }));
      }
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e) => {
    // Only prevent default if we were actually dragging
    if (isDragging) {
      e.preventDefault();
    }
    setIsDragging(false);
  };

  // Add event listeners for mouse events
  useEffect(() => {
    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, dragStart, mapTransform, handleMouseMove]);


  const handleTerritoryClick = (index) => {
    if (!canMakeMove() || isDragging) return;
    
    const territoryOwner = gameState.territoryOwners[index];
    const isMyTerritory = territoryOwner === gameState.currentPlayer;
    
    if (!moveInProgress) {
      // Starting a new move - select source
      if (isMyTerritory && getAvailableUnits(index) > 0) {
        setSelectedSource(index);
        setSelectedTarget(null);
        setMoveInProgress(true);
      }
    } else {
      // Move in progress
      if (index === selectedSource) {
        // Clicking same source - deselect and cancel move
        setSelectedSource(null);
        setSelectedTarget(null);
        setMoveInProgress(false);
      } else if (isValidTargetForSource(index)) {
        // Clicking valid adjacent target - show unit selection popup
        setSelectedTarget(index);
        
      } else {
        // Clicking invalid target - do nothing (territory should be non-clickable anyway)
        return;
      }
    }
  };

  const addMoveWithUnits = (unitCount) => {
    if (selectedSource === null || selectedTarget === null) return;
    
    setSelectedMoves([...selectedMoves, {
      fromTerritory: selectedSource,
      toTerritory: selectedTarget,
      unitCount: unitCount
    }]);
    
    // Reset selection state
    setSelectedSource(null);
    setSelectedTarget(null);
    setMoveInProgress(false);
    setCustomUnitInput('');
  };


  const cancelMove = () => {
    setSelectedSource(null);
    setSelectedTarget(null);
    setMoveInProgress(false);
    setCustomUnitInput('');
  };

  const submitTurn = async () => {
    // Allow submitting with no moves (pass turn)
    if (selectedMoves.length === 0) {
      const confirmPass = window.confirm('No moves selected. Do you want to pass your turn and only get reinforcements?');
      if (!confirmPass) return;
    }

    // Set transaction pending state
    setTransactionPending({ 
      type: 'submit', 
      numMoves: selectedMoves.length,
      message: selectedMoves.length === 0 ? 'Passing turn...' : `Submitting turn with ${selectedMoves.length} moves...`
    });

    try {
      // riskContract was created with createContract(address, abi, true) so it already has a signer
      const tx = await riskContract.submitTurn(gameIDNumber, selectedMoves, {
        gasLimit: 3000000
      });
      
      console.log('Turn submitted:', tx.hash);
      setSelectedMoves([]);
      
      // Wait for transaction to be confirmed (Ethers.js pattern)
      await tx.wait();
      
      // Reload game state from public mapping without full page refresh (contains all territory info)
      const gameInstance = await riskContract.gameInstances(gameIDNumber);
      const player1 = gameInstance.player1;
      const player2 = gameInstance.player2;
      const status = gameInstance.status.toString();
      const gameFinished = !gameInstance.gameActive;
      const lastMoveTime = safeToNumber(gameInstance.lastMoveTime);
      const contribution1 = gameInstance.contribution1;
      const contribution2 = gameInstance.contribution2;
      const currentPlayer = safeToNumber(gameInstance.currentPlayer);

      // Load territory data with bulk API call
      // Use same territory array size as current game state
      let territoryOwners = new Array(gameState.territoryOwners.length).fill(0);
      let territoryUnits = new Array(gameState.territoryUnits.length).fill(0);
      
      try {
        const territoryInfo = await riskContract.getTerritoryInfo(gameIDNumber);
        territoryOwners = territoryInfo.owners.map(owner => safeToNumber(owner));
        territoryUnits = territoryInfo.units.map(units => safeToNumber(units));
        console.log('Territory data reloaded after turn submission');
      } catch (error) {
        console.log('Submit turn: Game exists but territories not initialized yet.');
        console.error('Error reloading territory data:', error);
        // Keep default empty arrays
      }

      const newGameState = { 
        player1, 
        player2, 
        status, 
        gameFinished, 
        lastMoveTime, 
        contribution1, 
        contribution2,
        currentPlayer,
        territoryOwners,
        territoryUnits,
        mapType: mapParam
      };
      
      setGameState(prevState => ({
        ...prevState,
        ...newGameState
      }));
      
      if (!isViewingHistory) {
        setDisplayState(prevState => ({
          ...prevState,
          ...newGameState
        }));
      }
      
      console.log('Game state updated after turn submission');
      
      // Refresh history to include the new turn
      if (historyHook && historyHook.refreshHistory) {
        try {
          await historyHook.refreshHistory();
          console.log('Game history refreshed after turn submission');
        } catch (error) {
          console.error('Failed to refresh game history:', error);
        }
      }
    } catch (error) {
      console.error('Failed to submit turn:', error);
      alert('Failed to submit turn: ' + error.message);
      // Clear pending state on error since no blockchain event will be emitted
      setTransactionPending(null);
    } finally {
      // Transaction pending state will be cleared by event handler when turn is confirmed
    }
  };

  const canMakeMove = () => {
    // Can't make moves when viewing history
    if (isViewingHistory) return false;
    
    // Can't make moves when transaction is pending
    if (transactionPending) return false;
    
    const isMyTurn = (gameState.currentPlayer === 1 && userState.userAddress.toLowerCase() === gameState.player1.toLowerCase()) ||
                     (gameState.currentPlayer === 2 && userState.userAddress.toLowerCase() === gameState.player2.toLowerCase());
    return !gameState.gameFinished && isMyTurn;
  };

  const isMyTurn = () => {
    // Helper function to check if it's the user's turn (without transaction pending check)
    if (isViewingHistory) return false;
    
    const myTurn = (gameState.currentPlayer === 1 && userState.userAddress.toLowerCase() === gameState.player1.toLowerCase()) ||
                   (gameState.currentPlayer === 2 && userState.userAddress.toLowerCase() === gameState.player2.toLowerCase());
    return !gameState.gameFinished && myTurn;
  };

  const getPlayerTerritoryCount = (player) => {
    const currentViewState = displayState || gameState;
    return currentViewState.territoryOwners.filter(owner => owner === player).length;
  };

  const getCapturableTerritoryCount = () => {
    if (!mapData) return 0; // Return 0 during loading, will be updated when map loads
    return mapData.getCapturableTerritoryCount();
  };

  // Show full skeleton while UI is not ready
  if (!uiReady) {
    return <FullGameSkeleton gameType="risk" />;
  }

  // Show loading while wallet is initializing
  if (!isWalletInitialized) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-4xl font-bold text-gray-600 mb-4">Initializing...</h1>
        <p className="text-gray-500">Connecting to wallet...</p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          Error: {errorMessage}
        </div>
      </div>
    );
  }

  // Wallet handlers for TopRightBar
  const handleSwitchWallet = () => {
    // For individual game pages, we'll navigate back to main page for wallet switching
    window.location.href = '/';
  };

  const handleWalletDisconnect = async () => {
    try {
      const accounts = await getAccounts();
      setUserState(prev => ({ ...prev, userAddress: accounts[0] || '' }));
    } catch (error) {
      console.error('Error after wallet disconnect:', error);
      window.location.href = '/';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <style>
        {`
          .collapsible-button {
            background: white !important;
            background-color: white !important;
            background-image: none !important;
            color: #374151 !important;
            border: none !important;
            padding: 0 !important;
            border-radius: 0 !important;
          }
          .collapsible-container {
            background-color: white !important;
            border-radius: 0.5rem !important;
            padding: 1.5rem !important;
            margin-bottom: 1.5rem !important;
            border: none !important;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
          }
        `}
      </style>
      
      {/* Top Navigation Bar */}
      <div className="flex justify-between items-center mb-6">
        <Link
          to="/1on1"
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm transition"
        >
          ← Back to Games
        </Link>
        
        <TopRightBar 
          onSwitchWallet={handleSwitchWallet}
          onWalletDisconnect={handleWalletDisconnect}
          showInTestMode={true}
        />
      </div>
      
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-center mb-8 text-gray-800">
          Risk Game #{gameID} - {getMapDisplayName(gameState.mapType)}
        </h1>

        {/* Game Rules - Collapsible */}
        <div className="collapsible-container">
          <button 
            className="collapsible-button"
            onClick={() => setRulesCollapsed(!rulesCollapsed)}
            style={{ 
              width: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              fontSize: '1.25rem', 
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <span>How Risk Works</span>
            <svg className={`w-5 h-5 transition-transform duration-200 ${rulesCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            rulesCollapsed ? 'max-h-0 opacity-0' : 'max-h-96 opacity-100 mt-3'
          }`}>
            <ul className="list-disc list-inside space-y-2 text-gray-600">
              <li>Territories are randomly assigned to both players at game start</li>
              <li>Players take turns moving units between adjacent territories</li>
              <li>Must leave at least 1 unit in source territory when moving</li>
              <li>Combat: Each attacker has 1/3 chance to kill a defender</li>
              <li>If all defenders killed, attackers capture the territory</li>
              <li>After each turn, players get +1 unit per territory they control</li>
              <li>Win by controlling all capturable territories</li>
              <li>🔍 Find the contract info and verified code here: <AddressDisplayLong address={contractInfo.address} useExplorerLink={true} /></li>
            </ul>
          </div>
        </div>

        {/* Game Status */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">Game Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="text-lg font-medium">{getStatusMessage(gameState.status)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Your Role</p>
              <p className="text-lg font-medium">{getUserRole()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Current Turn</p>
              <p className="text-lg font-medium">Player {gameState.currentPlayer}</p>
            </div>
          </div>
          
        </div>

        {/* Game History Navigation */}
        {riskContract && (
          <RiskHistoryPanel 
            historyHook={historyHook}
            currentPlayerAddress={userState.userAddress}
          />
        )}

        {/* Combat Details for Historical Turns */}
        {historyHook.isViewingHistory && historyHook.currentTurnIndex >= 0 && (
          <RiskCombatDetails
            turnDetails={historyHook.getTurnDetails(historyHook.currentTurnIndex)}
            stateBefore={historyHook.turnHistory[historyHook.currentTurnIndex]?.stateBefore}
            stateAfter={historyHook.turnHistory[historyHook.currentTurnIndex]?.stateAfter}
            currentPlayerAddress={userState.userAddress}
          />
        )}

      </div>
      
      {/* Territory Map - Full Width */}
      <div className={`w-full mb-6 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}
           style={isFullscreen ? { backgroundColor: '#e1ecf7' } : {}}>
        <div className={`${isFullscreen ? '' : 'bg-white shadow-lg'} h-full relative`}>
          {!isFullscreen && (
            <h2 className="text-2xl font-semibold py-4 text-gray-700 text-center">
              World Map
            </h2>
          )}
          {(loadingStages.mapData || loadingStages.gameDetails) && (
            <div className="w-full h-96 flex items-center justify-center">
              <div className="animate-pulse bg-gray-300 w-full h-full rounded flex items-center justify-center">
                <div className="text-gray-500">Loading map...</div>
              </div>
            </div>
          )}
          {/* Hexagonal Map */}
          {!loadingStages.mapData && !loadingStages.gameDetails && mapData && (
            <div className={`w-full relative ${isFullscreen ? 'h-full' : ''}`}>

              {/* History Mode Overlay */}
              {isViewingHistory && (
                <div className="absolute inset-0 bg-gray-50 bg-opacity-95 flex items-center justify-center z-50">
                  <div className="text-center p-8">
                    <h3 className="text-2xl font-semibold text-gray-700 mb-4">History Mode</h3>
                    <p className="text-gray-600 mb-4">
                      Map display is disabled while viewing game history.
                    </p>
                    <p className="text-sm text-gray-500">
                      Use the history panel below to navigate through turns and view combat details.
                    </p>
                  </div>
                </div>
              )}

              <svg
              ref={svgRef}
              width={mapData?.MAP_SETTINGS.mapWidth || 320} 
              height={mapData?.MAP_SETTINGS.mapHeight || 290} 
              viewBox={mapData?.MAP_SETTINGS ? 
                `${mapData.MAP_SETTINGS.viewBoxX || 0} ${mapData.MAP_SETTINGS.viewBoxY || 0} ${mapData.MAP_SETTINGS.viewBoxWidth || mapData.MAP_SETTINGS.mapWidth} ${mapData.MAP_SETTINGS.viewBoxHeight || mapData.MAP_SETTINGS.mapHeight}` :
                `0 0 320 290`
              }
              className={`w-full ${isFullscreen ? '' : 'border-t border-b border-gray-300 bg-blue-50'}`}
              style={{ 
                height: isFullscreen ? 'calc(100vh - 100px)' : 'auto',
                cursor: isDragging ? 'grabbing' : 'grab'
              }}
              onMouseDown={handleMouseDown}
              onWheel={handleWheel}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Background ocean - only in normal mode */}
              {!isFullscreen && (
                <rect 
                  x={mapData?.MAP_SETTINGS?.viewBoxX || 0}
                  y={mapData?.MAP_SETTINGS?.viewBoxY || 0}
                  width={mapData?.MAP_SETTINGS?.viewBoxWidth || mapData?.MAP_SETTINGS?.mapWidth || 320} 
                  height={mapData?.MAP_SETTINGS?.viewBoxHeight || mapData?.MAP_SETTINGS?.mapHeight || 290} 
                  fill="#4A90E2" 
                  fillOpacity="0.2" 
                />
              )}
              
              {/* Triangle marker definitions */}
              <defs>
                <marker
                  id="triangle-red"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                >
                  <polygon
                    points="1 1, 5 3, 1 5"
                    fill="#dc2626"
                  />
                </marker>
                <marker
                  id="triangle-green"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                >
                  <polygon
                    points="1 1, 5 3, 1 5"
                    fill="#16a34a"
                  />
                </marker>
                <marker
                  id="triangle-blue"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                >
                  <polygon
                    points="1 1, 5 3, 1 5"
                    fill="#2563eb"
                  />
                </marker>
              </defs>
              
              {/* Map content with zoom and pan transform */}
              <g transform={`translate(${mapTransform.panX}, ${mapTransform.panY}) scale(${mapTransform.zoom})`}>
                {/* Territory hexagons */}
              {(displayState || gameState).territoryOwners.map((owner, index) => {
                const midpoint = mapData.getTerritoryMidpoint(index);
                const shape = mapData.getTerritoryShape(index);
                if (!midpoint || !shape) return null;

                // Check if territory is impassable mountain or lake
                const territory = mapData.MAP_CONFIG.territories.find(t => t.id === index);
                const isImpassableMountain = territory && territory.tiletype === 'Mountain';
                const isLake = territory && territory.tiletype === 'Lake';

                // Use midpoint for text positioning
                const centerX = midpoint.x;
                const centerY = midpoint.y;

                const availableUnits = getAvailableUnits(index);
                const currentViewState = displayState || gameState;
                const isMyTerritory = owner === currentViewState.currentPlayer;

                
                let isClickable = false;
                if (!isImpassableMountain && !isLake && canMakeMove()) {
                  if (!moveInProgress) {
                    isClickable = isMyTerritory && availableUnits > 0;
                  } else {
                    isClickable = (index === selectedSource) || isValidTargetForSource(index);
                  }
                }
                
                // Get colors
                let fillColor = '#d1d5db'; // gray-300
                let strokeColor = '#9ca3af'; // gray-400
                
                // Mountains get special styling
                if (isImpassableMountain) {
                  fillColor = '#6b7280'; // gray-500 - darker gray for mountains
                  strokeColor = '#374151'; // gray-700
                } else if (isLake) {
                  fillColor = '#0ea5e9'; // sky-500 - blue for lake
                  strokeColor = '#0284c7'; // sky-600
                } else if (selectedSource === index) {
                  fillColor = owner === 1 ? '#1d4ed8' : '#dc2626'; // blue-700 or red-600
                } else if (selectedTarget === index) {
                  fillColor = owner === 1 ? '#3b82f6' : '#ef4444'; // blue-500 or red-500
                } else if (moveInProgress && selectedSource !== null && isValidTargetForSource(index)) {
                  // Keep original color but make it more vibrant/saturated
                  switch (owner) {
                    case 1: 
                      fillColor = '#2563eb'; // More vibrant blue (blue-600)
                      strokeColor = '#1d4ed8'; // blue-700
                      break;
                    case 2: 
                      fillColor = '#dc2626'; // More vibrant red (red-600) 
                      strokeColor = '#b91c1c'; // red-700
                      break;
                    default: 
                      fillColor = '#6b7280'; // More vibrant gray (gray-500)
                      strokeColor = '#374151'; // gray-700
                      break;
                  }
                } else if (selectedMoves.find(move => move.fromTerritory === index)) {
                  fillColor = owner === 1 ? '#93c5fd' : '#fca5a5'; // blue-300 or red-300
                } else {
                  // Territory colors based on ownership
                  if (owner === 1) {
                    fillColor = '#3b82f6'; // blue-500 for player 1
                  } else if (owner === 2) {
                    fillColor = '#ef4444'; // red-500 for player 2
                  } else {
                    // For unowned territories (owner === 0), try to infer ownership from unit count
                    const territoryUnits = currentViewState.territoryUnits[index];
                    if (isViewingHistory && territoryUnits > 0) {
                      // In history mode, if territory has units but no owner, guess ownership
                      // This is a fallback for data consistency issues

                      // Check neighboring territories to guess ownership
                      const adjacencies = mapData?.getAdjacencies?.(index) || [];
                      const neighborOwners = adjacencies
                        .map(neighborId => currentViewState.territoryOwners[neighborId])
                        .filter(neighborOwner => neighborOwner === 1 || neighborOwner === 2);

                      if (neighborOwners.length > 0) {
                        // Use most common neighbor ownership
                        const player1Count = neighborOwners.filter(o => o === 1).length;
                        const player2Count = neighborOwners.filter(o => o === 2).length;
                        fillColor = player1Count >= player2Count ? '#3b82f6' : '#ef4444';
                      } else {
                        // Default to grey if can't determine
                        fillColor = '#d1d5db'; // gray-300
                      }
                    } else {
                      // Truly unowned territory
                      fillColor = '#d1d5db'; // gray-300
                    }
                  }
                }
                
                return (
                  <g key={index}>
                    {/* Territory polygon */}
                    <polygon
                      points={mapData.getPolygonPath(shape)}
                      fill={fillColor}
                      stroke={strokeColor}
                      strokeWidth="1"
                      className={`territory ${isClickable ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed opacity-60'}`}
                      data-id={index}
                      id={`territory-${index}`}
                      onClick={() => handleTerritoryClick(index)}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        handleTerritoryClick(index);
                      }}
                    />
                    
                    {/* Territory Name */}
                    <text
                      x={centerX}
                      y={centerY - 6}
                      textAnchor="middle"
                      className="text-xs font-bold fill-white pointer-events-none"
                      style={{ fontSize: '8px' }}
                    >
                      {isImpassableMountain || isLake 
                        ? mapData.getTerritoryName(index).replace(/[LM]$/, '') 
                        : mapData.getTerritoryName(index)}
                    </text>
                    
                    {/* Special icons for mountains and lakes, unit count for playable territories */}
                    {isImpassableMountain ? (
                      <text
                        x={centerX}
                        y={centerY + 12}
                        textAnchor="middle"
                        className="text-lg fill-white pointer-events-none"
                        style={{ fontSize: '16px' }}
                      >
                        ⛰️
                      </text>
                    ) : isLake ? (
                      <text
                        x={centerX}
                        y={centerY + 12}
                        textAnchor="middle"
                        className="text-lg fill-white pointer-events-none"
                        style={{ fontSize: '16px' }}
                      >
                        🌊
                      </text>
                    ) : (
                      <text
                        x={centerX}
                        y={centerY + 10}
                        textAnchor="middle"
                        className="text-xs fill-white pointer-events-none"
                        style={{ fontSize: '11px' }}
                      >
                        {isViewingHistory ? (displayState || gameState).territoryUnits[index] : getDisplayUnits(index)}
                        {!isViewingHistory && getDisplayUnits(index) !== gameState.territoryUnits[index] && (
                          <tspan className="fill-yellow-200">
                          ({gameState.territoryUnits[index] > getDisplayUnits(index) ? '-' : '+'}
                          {Math.abs(gameState.territoryUnits[index] - getDisplayUnits(index))})
                        </tspan>
                      )}
                      </text>
                    )}
                    
                    {/* Territory name on hover (title) */}
                    <title>
                      {isImpassableMountain 
                        ? `${mapData.getTerritoryName(index).replace(/[LM]$/, '')} - Impassable Mountain ⛰️`
                        : isLake
                        ? `${mapData.getTerritoryName(index).replace(/[LM]$/, '')} - Traversable Lake 🌊 (crossing point)`
                        : `${mapData.getTerritoryName(index)} - ${gameState.territoryUnits[index]} units (${availableUnits} available)`}
                    </title>
                  </g>
                );
              })}
              
              {/* Connection Lines - Always shown, thicker for planned moves */}
              {mapData?.CONNECTION_LINES && Object.entries(mapData.CONNECTION_LINES).map(([connectionKey, connectionData]) => {
                const visualLines = connectionData.visualLines || [];
                
                return visualLines.map((line, lineIndex) => {
                  if (!line.from || !line.to) return null;
                  
                  const fromX = line.from.x;
                  const fromY = line.from.y;
                  const toX = line.to.x;
                  const toY = line.to.y;
                  
                  // Check if this connection represents a planned move
                  const [fromTerr, toTerr] = connectionKey.split('-').map(id => parseInt(id));
                  const isPlannedMove = selectedMoves.some(move => 
                    (move.fromTerritory === fromTerr && move.toTerritory === toTerr) ||
                    (move.fromTerritory === toTerr && move.toTerritory === fromTerr)
                  );
                  
                  // Check if this connection involves selected territories
                  const isCurrentSelection = 
                    (selectedSource === fromTerr && selectedTarget === toTerr) ||
                    (selectedSource === toTerr && selectedTarget === fromTerr);
                  
                  // Determine line style based on connection type
                  const isOceanCrossing = connectionData.connectionType === 'ocean_crossing';
                  let strokeColor, strokeWidth, strokeDashArray, strokeOpacity;
                  
                  if (isCurrentSelection) {
                    // Bright highlighting for current selection
                    strokeColor = '#fbbf24'; // amber-400
                    strokeWidth = '4';
                    strokeDashArray = isOceanCrossing ? '8,4' : 'none';
                    strokeOpacity = '1';
                  } else if (isPlannedMove) {
                    // Medium highlighting for planned moves
                    strokeColor = '#10b981'; // emerald-500
                    strokeWidth = '3';
                    strokeDashArray = isOceanCrossing ? '6,3' : 'none';
                    strokeOpacity = '0.9';
                  } else {
                    // Default connection line style
                    strokeColor = isOceanCrossing ? '#0066cc' : '#666';
                    strokeWidth = isOceanCrossing ? '1.5' : '1';
                    strokeDashArray = isOceanCrossing ? '4,2' : 'none';
                    strokeOpacity = '0.7';
                  }
                  
                  return (
                    <line
                      key={`${connectionKey}-${lineIndex}`}
                      x1={fromX}
                      y1={fromY}
                      x2={toX}
                      y2={toY}
                      stroke={strokeColor}
                      strokeWidth={strokeWidth}
                      strokeDasharray={strokeDashArray}
                      strokeOpacity={strokeOpacity}
                      className="connection-line"
                    />
                  );
                });
              })}
              
              {/* Arrow for current selection */}
              {selectedSource !== null && selectedTarget !== null && (
                (() => {
                  const sourceMidpoint = mapData.getTerritoryMidpoint(selectedSource);
                  const targetMidpoint = mapData.getTerritoryMidpoint(selectedTarget);
                  if (!sourceMidpoint || !targetMidpoint) return null;
                  
                  const sourceX = sourceMidpoint.x;
                  const sourceY = sourceMidpoint.y;
                  const targetX = targetMidpoint.x;
                  const targetY = targetMidpoint.y;
                  
                  // Calculate arrow direction
                  const dx = targetX - sourceX;
                  const dy = targetY - sourceY;
                  const length = Math.sqrt(dx * dx + dy * dy);
                  
                  // Shorten arrow to not overlap territory centers
                  const shortenBy = 20; // Fixed value for polygon territories
                  const ratio = (length - shortenBy) / length;
                  const endX = sourceX + dx * ratio;
                  const endY = sourceY + dy * ratio;
                  const startX = sourceX + dx * (shortenBy / length);
                  const startY = sourceY + dy * (shortenBy / length);
                  
                  // Use target territory color
                  const targetOwner = gameState.territoryOwners[selectedTarget];
                  const arrowColor = targetOwner === 1 ? '#3b82f6' : '#ef4444'; // blue for player 1, red for player 2
                  const markerEnd = targetOwner === 1 ? 'url(#triangle-blue)' : 'url(#triangle-red)';
                  
                  // Selection arrow uses same style as planned moves but without unit count
                  return (
                    <g>
                      {/* Arrow line with triangle marker */}
                      <line
                        x1={startX}
                        y1={startY}
                        x2={endX}
                        y2={endY}
                        stroke={arrowColor}
                        strokeWidth="2.5"
                        strokeOpacity="0.95"
                        markerEnd={markerEnd}
                      />
                      
                      {/* Triangle background */}
                      <polygon
                        points={(() => {
                          // Calculate triangle direction (same as movement)
                          const moveX = endX - startX;
                          const moveY = endY - startY;
                          const moveLength = Math.sqrt(moveX * moveX + moveY * moveY);
                          const dirX = moveX / moveLength;
                          const dirY = moveY / moveLength;
                          
                          // Triangle dimensions
                          const triangleSize = 13;
                          
                          // Shift triangle towards target so geometric center is at midpoint
                          const midpointX = (startX + endX) / 2;
                          const midpointY = (startY + endY) / 2;
                          // Triangle's geometric center is 1/3 from base, so shift triangle forward by 1/6 of its length
                          const shiftDistance = triangleSize / 6;
                          const centerX = midpointX + dirX * shiftDistance;
                          const centerY = midpointY + dirY * shiftDistance;
                          
                          // Calculate triangle points oriented in movement direction
                          const perpX = -dirY; // Perpendicular to movement direction
                          const perpY = dirX;
                          
                          // Triangle tip (pointing forward)
                          const tipX = centerX + dirX * triangleSize / 2;
                          const tipY = centerY + dirY * triangleSize / 2;
                          
                          // Triangle base points
                          const baseX = centerX - dirX * triangleSize / 2;
                          const baseY = centerY - dirY * triangleSize / 2;
                          const base1X = baseX + perpX * triangleSize / 2;
                          const base1Y = baseY + perpY * triangleSize / 2;
                          const base2X = baseX - perpX * triangleSize / 2;
                          const base2Y = baseY - perpY * triangleSize / 2;
                          
                          return `${tipX},${tipY} ${base1X},${base1Y} ${base2X},${base2Y}`;
                        })()}
                        fill="white"
                        stroke={arrowColor}
                        strokeWidth="1.5"
                        fillOpacity="0.98"
                        filter="drop-shadow(1px 1px 2px rgba(0,0,0,0.3))"
                      />
                    </g>
                  );
                })()
              )}

              {/* Movement arrows for planned moves */}
              {selectedMoves.map((move, index) => {
                const sourceMidpoint = mapData.getTerritoryMidpoint(move.fromTerritory);
                const targetMidpoint = mapData.getTerritoryMidpoint(move.toTerritory);
                if (!sourceMidpoint || !targetMidpoint) return null;
                
                const sourceX = sourceMidpoint.x;
                const sourceY = sourceMidpoint.y;
                const targetX = targetMidpoint.x;
                const targetY = targetMidpoint.y;
                
                // Calculate arrow direction
                const dx = targetX - sourceX;
                const dy = targetY - sourceY;
                const length = Math.sqrt(dx * dx + dy * dy);
                
                // Shorten arrow to not overlap territory centers
                const shortenBy = 20; // Fixed value for polygon territories
                const ratio = (length - shortenBy) / length;
                const endX = sourceX + dx * ratio;
                const endY = sourceY + dy * ratio;
                const startX = sourceX + dx * (shortenBy / length);
                const startY = sourceY + dy * (shortenBy / length);
                
                // Use target territory color
                const targetOwner = gameState.territoryOwners[move.toTerritory];
                const arrowColor = targetOwner === 1 ? '#3b82f6' : '#ef4444'; // blue for player 1, red for player 2
                const markerEnd = targetOwner === 1 ? 'url(#triangle-blue)' : 'url(#triangle-red)';
                
                return (
                  <g key={`arrow-${index}`}>
                    {/* Arrow line with proper arrowhead - thicker and more prominent */}
                    <line
                      x1={startX}
                      y1={startY}
                      x2={endX}
                      y2={endY}
                      stroke={arrowColor}
                      strokeWidth="2.5"
                      strokeOpacity="0.95"
                      markerEnd={markerEnd}
                    />
                    
                    {/* Triangle unit count indicator */}
                    <g>
                      {/* Triangle background pointing in movement direction */}
                      <polygon
                        points={(() => {
                          // Calculate triangle direction (same as movement)
                          const moveX = endX - startX;
                          const moveY = endY - startY;
                          const moveLength = Math.sqrt(moveX * moveX + moveY * moveY);
                          const dirX = moveX / moveLength;
                          const dirY = moveY / moveLength;
                          
                          // Triangle dimensions
                          const triangleSize = 13;
                          
                          // Shift triangle towards target so geometric center is at midpoint
                          const midpointX = (startX + endX) / 2;
                          const midpointY = (startY + endY) / 2;
                          // Triangle's geometric center is 1/3 from base, so shift triangle forward by 1/6 of its length
                          const shiftDistance = triangleSize / 6;
                          const centerX = midpointX + dirX * shiftDistance;
                          const centerY = midpointY + dirY * shiftDistance;
                          
                          // Calculate triangle points oriented in movement direction
                          const perpX = -dirY; // Perpendicular to movement direction
                          const perpY = dirX;
                          
                          // Triangle tip (pointing forward)
                          const tipX = centerX + dirX * triangleSize / 2;
                          const tipY = centerY + dirY * triangleSize / 2;
                          
                          // Triangle base points
                          const baseX = centerX - dirX * triangleSize / 2;
                          const baseY = centerY - dirY * triangleSize / 2;
                          const base1X = baseX + perpX * triangleSize / 2;
                          const base1Y = baseY + perpY * triangleSize / 2;
                          const base2X = baseX - perpX * triangleSize / 2;
                          const base2Y = baseY - perpY * triangleSize / 2;
                          
                          return `${tipX},${tipY} ${base1X},${base1Y} ${base2X},${base2Y}`;
                        })()}
                        fill="white"
                        stroke={arrowColor}
                        strokeWidth="1.5"
                        fillOpacity="0.98"
                        filter="drop-shadow(1px 1px 2px rgba(0,0,0,0.3))"
                      />
                      {/* Unit count text - positioned in arrow center */}
                      <text
                        x={(startX + endX) / 2}
                        y={(startY + endY) / 2 + 3}
                        textAnchor="middle"
                        className="font-bold pointer-events-none"
                        fill={arrowColor}
                        style={{ fontSize: '6.7px', fontWeight: 'bold' }}
                      >
                        {move.unitCount}
                      </text>
                    </g>
                  </g>
                );
              })}

              {/* Combat indicators for history mode */}
              {isViewingHistory && historyHook.currentTurnIndex >= 0 && (() => {
                const turnDetails = historyHook.getTurnDetails(historyHook.currentTurnIndex);
                if (!turnDetails?.events) return null;

                // Get all combat events for this turn
                const combatEvents = turnDetails.events.filter(e => e.type === 'Combat');
                const conquestEvents = turnDetails.events.filter(e => e.type === 'TerritoryConquered');

                return (
                  <g>
                    {/* Combat territory indicators */}
                    {combatEvents.map((combat, index) => {
                      const territoryId = combat.args.territory;
                      const midpoint = mapData.getTerritoryMidpoint(territoryId);
                      if (!midpoint) return null;

                      return (
                        <g key={`combat-${index}`}>
                          {/* Combat flash indicator */}
                          <circle
                            cx={midpoint.x}
                            cy={midpoint.y}
                            r="8"
                            fill="none"
                            stroke="#ff6b35"
                            strokeWidth="2.5"
                            strokeOpacity="0.8"
                            className="animate-pulse"
                          />
                          <text
                            x={midpoint.x}
                            y={midpoint.y + 2}
                            textAnchor="middle"
                            className="text-xs pointer-events-none"
                            fill="#ff6b35"
                            style={{ fontSize: '10px', fontWeight: 'bold' }}
                          >
                            ⚔️
                          </text>
                        </g>
                      );
                    })}

                    {/* Territory conquest indicators */}
                    {conquestEvents.map((conquest, index) => {
                      const territoryId = conquest.args.territory;
                      const midpoint = mapData.getTerritoryMidpoint(territoryId);
                      if (!midpoint) return null;

                      return (
                        <g key={`conquest-${index}`}>
                          {/* Conquest indicator */}
                          <circle
                            cx={midpoint.x}
                            cy={midpoint.y - 15}
                            r="6"
                            fill="#10b981"
                            stroke="#065f46"
                            strokeWidth="1.5"
                            fillOpacity="0.9"
                          />
                          <text
                            x={midpoint.x}
                            y={midpoint.y - 12}
                            textAnchor="middle"
                            className="text-xs pointer-events-none"
                            fill="white"
                            style={{ fontSize: '8px', fontWeight: 'bold' }}
                          >
                            🏁
                          </text>
                        </g>
                      );
                    })}
                  </g>
                );
              })()}

              {/* Unit Selection Interface - Outside zoom/pan group so it doesn't scale but moves with map */}
              {selectedSource !== null && selectedTarget !== null && (
                (() => {
                  const sourceMidpoint = mapData.getTerritoryMidpoint(selectedSource);
                  const targetMidpoint = mapData.getTerritoryMidpoint(selectedTarget);
                  if (!sourceMidpoint || !targetMidpoint) return null;
                  
                  const sourceCenterX = sourceMidpoint.x;
                  const sourceCenterY = sourceMidpoint.y;
                  const targetCenterX = targetMidpoint.x;
                  const targetCenterY = targetMidpoint.y;
                  
                  // Calculate midpoint
                  const midX = (sourceCenterX + targetCenterX) / 2;
                  const midY = (sourceCenterY + targetCenterY) / 2;
                  
                  // Calculate perpendicular offset
                  const dx = targetCenterX - sourceCenterX;
                  const dy = targetCenterY - sourceCenterY;
                  const distance = Math.sqrt(dx * dx + dy * dy);
                  if (distance === 0) return null;
                  
                  const perpX = -dy / distance;
                  const perpY = dx / distance;
                  
                  const offset = 70;
                  let panelX = midX + perpX * offset;
                  let panelY = midY + perpY * offset;
                    
                  const unitButtons = getUnitSelectionButtons();
                  const buttonWidth = 14; // 50% of 28
                  const buttonHeight = 11; // 50% of 22
                  const buttonSpacing = 1; // 50% of 2
                  const inputWidth = 20; // 50% of 40
                  const cancelWidth = 10; // 50% of 20
                  
                  // Calculate total width: 4 buttons + input + cancel + spacing
                  const totalContentWidth = 4 * buttonWidth + inputWidth + cancelWidth + 5 * buttonSpacing;
                  const panelWidth = Math.max(100, totalContentWidth + 2); // 50% of min 200, 50% of padding
                  const panelHeight = 22; // 50% of 45
                  
                  // Boundary checking - keep panel within visible viewport
                  const halfPanelWidth = panelWidth / 2;
                  const halfPanelHeight = panelHeight / 2;
                  
                  // Calculate visible boundaries based on current zoom and pan
                  // The visible area in map coordinates
                  const visibleLeft = -mapTransform.panX / mapTransform.zoom;
                  const visibleTop = -mapTransform.panY / mapTransform.zoom;
                  const visibleRight = visibleLeft + ((mapData?.MAP_SETTINGS?.mapWidth || 320) / mapTransform.zoom);
                  const visibleBottom = visibleTop + ((mapData?.MAP_SETTINGS?.mapHeight || 290) / mapTransform.zoom);
                  
                  // Clamp to visible boundaries
                  panelX = Math.max(visibleLeft + halfPanelWidth, Math.min(visibleRight - halfPanelWidth, panelX));
                  panelY = Math.max(visibleTop + halfPanelHeight, Math.min(visibleBottom - halfPanelHeight, panelY));
                  
                  return (
                    <g>
                      {/* Panel Background */}
                      <rect
                        x={panelX - panelWidth / 2}
                        y={panelY - panelHeight / 2}
                        width={panelWidth}
                        height={panelHeight}
                        fill="white"
                        stroke="#3b82f6"
                        strokeWidth="2"
                        rx="8"
                        fillOpacity="0.95"
                        filter="drop-shadow(2px 2px 4px rgba(0,0,0,0.2))"
                      />
                      
                      {/* Title */}
                      <text
                        x={panelX}
                        y={panelY - 6}
                        textAnchor="middle"
                        className="fill-gray-700 text-xs font-medium"
                        style={{ fontSize: '5px' }}
                      >
                        Select units to move
                      </text>
                      
                      {/* Unit Selection Buttons - max 4 */}
                      {unitButtons.slice(0, 4).map((units, index) => {
                        const startX = panelX - totalContentWidth / 2;
                        const buttonX = startX + index * (buttonWidth + buttonSpacing);
                        const buttonY = panelY;
                        
                        return (
                          <g key={units}>
                            <rect
                              x={buttonX}
                              y={buttonY}
                              width={buttonWidth}
                              height={buttonHeight}
                              fill="#3b82f6"
                              stroke="#1d4ed8"
                              strokeWidth="1"
                              rx="4"
                              className="cursor-pointer hover:fill-blue-600"
                              onClick={() => addMoveWithUnits(units)}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                addMoveWithUnits(units);
                              }}
                            />
                            <text
                              x={buttonX + buttonWidth / 2}
                              y={buttonY + buttonHeight / 2 + 1.5}
                              textAnchor="middle"
                              className="fill-white text-xs font-bold pointer-events-none"
                              style={{ fontSize: '5.5px' }}
                            >
                              {units}
                            </text>
                          </g>
                        );
                      })}
                      
                      {/* Custom Input Section */}
                      <g>
                        {/* Input Background */}
                        <rect
                          x={panelX - totalContentWidth / 2 + 4 * (buttonWidth + buttonSpacing)}
                          y={panelY}
                          width={inputWidth}
                          height={buttonHeight}
                          fill="white"
                          stroke="#9ca3af"
                          strokeWidth="1"
                          rx="4"
                        />
                        {/* Input Text */}
                        <text
                          x={panelX - totalContentWidth / 2 + 4 * (buttonWidth + buttonSpacing) + inputWidth / 2}
                          y={panelY + buttonHeight / 2 + 1.5}
                          textAnchor="middle"
                          className="fill-gray-700 text-xs pointer-events-none"
                          style={{ fontSize: '5px' }}
                        >
                          {customUnitInput || 'Custom'}
                        </text>
                        {/* Invisible input area for click handling */}
                        <rect
                          x={panelX - totalContentWidth / 2 + 4 * (buttonWidth + buttonSpacing)}
                          y={panelY}
                          width={inputWidth}
                          height={buttonHeight}
                          fill="transparent"
                          className="cursor-pointer"
                          onClick={() => {
                            const input = prompt(`Enter number of units (1-${getAvailableUnits(selectedSource)}):`);
                            if (input !== null) {
                              setCustomUnitInput(input);
                              const unitCount = parseInt(input);
                              if (!isNaN(unitCount) && unitCount > 0 && unitCount <= getAvailableUnits(selectedSource)) {
                                addMoveWithUnits(unitCount);
                              }
                            }
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            const input = prompt(`Enter number of units (1-${getAvailableUnits(selectedSource)}):`);
                            if (input !== null) {
                              setCustomUnitInput(input);
                              const unitCount = parseInt(input);
                              if (!isNaN(unitCount) && unitCount > 0 && unitCount <= getAvailableUnits(selectedSource)) {
                                addMoveWithUnits(unitCount);
                              }
                            }
                          }}
                        />
                      </g>
                      
                      {/* Cancel Button */}
                      <g>
                        <rect
                          x={panelX - totalContentWidth / 2 + 4 * (buttonWidth + buttonSpacing) + inputWidth + buttonSpacing}
                          y={panelY}
                          width={cancelWidth}
                          height={buttonHeight}
                          fill="#ef4444"
                          stroke="#dc2626"
                          strokeWidth="1"
                          rx="4"
                          className="cursor-pointer hover:fill-red-600"
                          onClick={cancelMove}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            cancelMove();
                          }}
                        />
                        <text
                          x={panelX - totalContentWidth / 2 + 4 * (buttonWidth + buttonSpacing) + inputWidth + buttonSpacing + cancelWidth / 2}
                          y={panelY + buttonHeight / 2 + 1.5}
                          textAnchor="middle"
                          className="fill-white text-xs font-bold pointer-events-none"
                          style={{ fontSize: '5px' }}
                        >
                          ✕
                        </text>
                      </g>
                    </g>
                  );
                })()
              )}
              </g>
            </svg>
            
            {/* Zoom Controls */}
            <div className="absolute top-4 right-4 flex flex-col gap-2">
              <button
                onClick={zoomIn}
                className="bg-white border-2 border-gray-400 rounded-lg p-3 shadow-lg hover:bg-gray-50 hover:border-gray-500 transition-colors"
                title="Zoom In"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </button>
              <button
                onClick={zoomOut}
                className="bg-white border-2 border-gray-400 rounded-lg p-3 shadow-lg hover:bg-gray-50 hover:border-gray-500 transition-colors"
                title="Zoom Out"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
                </svg>
              </button>
              <button
                onClick={resetView}
                className="bg-white border-2 border-gray-400 rounded-lg p-3 shadow-lg hover:bg-gray-50 hover:border-gray-500 transition-colors"
                title="Reset View"
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="bg-white border-2 border-gray-400 rounded-lg p-3 shadow-lg hover:bg-gray-50 hover:border-gray-500 transition-colors"
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen Map"}
              >
                <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isFullscreen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M15 9V4.5M15 9h4.5M15 9l5.25-5.25M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 15v4.5m0-4.5h4.5m-4.5 0l5.25 5.25" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                  )}
                </svg>
              </button>
            </div>
            
            {/* Zoom level indicator */}
            <div className="absolute bottom-4 right-4 bg-white border-2 border-gray-400 rounded-lg px-3 py-2 shadow-lg text-sm text-gray-700 font-medium">
              Zoom: {Math.round(mapTransform.zoom * 100)}%
            </div>
          </div>
          )}

          {/* Move selection status - simplified */}
          {moveInProgress && selectedSource !== null && selectedTarget === null && (
            <div className="text-center mb-4 bg-blue-50 p-3 rounded-lg">
              <p className="text-lg font-medium text-blue-800">
                Source: {mapData?.getTerritoryName(selectedSource)} ({getAvailableUnits(selectedSource)} units available)
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Click an adjacent territory (highlighted in green) or click the source again to cancel
              </p>
            </div>
          )}

          {gameState.gameFinished && (
            <div className="text-center">
              <p className="text-xl font-semibold text-gray-700 mb-2">
                {getWinnerMessage()}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Constrained width content continues */}
      <div className="max-w-6xl mx-auto">
        {/* Move Planning */}
        {isMyTurn() && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-semibold mb-4 text-gray-700">Plan Your Moves</h2>
            <p className="text-sm text-gray-600 mb-4">
              Select territories and plan your moves, or pass your turn to only receive reinforcements.
            </p>
            
            {/* Transaction Pending Message */}
            {transactionPending && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600 mr-2"></div>
                  <span className="text-yellow-800 font-medium">{transactionPending.message}</span>
                </div>
                <p className="text-yellow-700 text-sm mt-1">
                  Please wait while your transaction is processed. Do not refresh the page.
                </p>
              </div>
            )}
            
            {selectedMoves.length > 0 && (
              <div className="mb-4">
                <h3 className="text-lg font-medium mb-2">Planned Moves:</h3>
                <div className="space-y-2">
                  {selectedMoves.map((move, index) => {
                    const sourceOwner = gameState.territoryOwners[move.fromTerritory];
                    const targetOwner = gameState.territoryOwners[move.toTerritory];
                    const sourceColor = sourceOwner === 1 ? 'text-blue-600' : 'text-red-600';
                    const targetColor = targetOwner === 1 ? 'text-blue-600' : 'text-red-600';
                    
                    return (
                    <div key={index} className="bg-gray-50 p-3 rounded flex justify-between items-center">
                      <span className="flex items-center">
                        <span className={`font-medium ${sourceColor}`}>
                          {mapData?.getTerritoryName(move.fromTerritory)}
                        </span>
                        <svg className="w-4 h-4 mx-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                        <span className={`font-medium ${targetColor}`}>
                          {mapData?.getTerritoryName(move.toTerritory)}
                        </span>
                        <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-bold">
                          {move.unitCount} units
                        </span>
                      </span>
                      <button
                        onClick={() => setSelectedMoves(selectedMoves.filter((_, i) => i !== index))}
                        className="text-red-500 hover:text-red-700 text-sm px-2 py-1 rounded hover:bg-red-50"
                        disabled={transactionPending}
                      >
                        ✕
                      </button>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={submitTurn}
                disabled={transactionPending}
                className={`px-6 py-2 rounded transition ${
                  transactionPending 
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                {transactionPending 
                  ? `Processing...` 
                  : selectedMoves.length === 0 ? 'Pass Turn (No Moves)' : `Submit Turn (${selectedMoves.length} moves)`
                }
              </button>
              <button
                onClick={() => setSelectedMoves([])}
                disabled={transactionPending}
                className={`px-4 py-2 rounded transition ${
                  transactionPending
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* Players Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Player 1 */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold mb-3 text-gray-700">Player 1 (Blue)</h3>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-mono text-xs break-all"><AddressDisplayLong address={gameState.player1} useExplorerLink={true} /></p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bet Amount</p>
                <p className="text-lg font-medium">{formatEth(gameState.contribution1)} POL</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Territories</p>
                <p className="text-lg font-medium">{getPlayerTerritoryCount(1)}/{getCapturableTerritoryCount()}</p>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-2">
                  <span className="text-white font-bold">1</span>
                </div>
                <span className="text-gray-700">Blue territories</span>
              </div>
              {gameState.status === '3' && (
                <div className="text-green-600 font-semibold">🎉 Winner!</div>
              )}
            </div>
          </div>

          {/* Player 2 */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold mb-3 text-gray-700">Player 2 (Red)</h3>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-mono text-xs break-all"><AddressDisplayLong address={gameState.player2} useExplorerLink={true} /></p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bet Amount</p>
                <p className="text-lg font-medium">{formatEth(gameState.contribution2)} POL</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Territories</p>
                <p className="text-lg font-medium">{getPlayerTerritoryCount(2)}/{getCapturableTerritoryCount()}</p>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-2">
                  <span className="text-white font-bold">2</span>
                </div>
                <span className="text-gray-700">Red territories</span>
              </div>
              {gameState.status === '4' && (
                <div className="text-green-600 font-semibold">🎉 Winner!</div>
              )}
            </div>
          </div>
        </div>

        {/* Game Details */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-semibold mb-3 text-gray-700">Game Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-600">Game ID</p>
              <p className="font-mono">{gameID}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Pot</p>
              <p className="text-lg font-medium">
                {formatEth((parseInt(gameState.contribution1) + parseInt(gameState.contribution2)).toString())} POL
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Move</p>
              <p>{gameState.lastMoveTime > 0 ? new Date(gameState.lastMoveTime * 1000).toLocaleString() : 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Contract Address</p>
              <div className="font-mono text-xs"><AddressDisplayLong address={contractInfo.address} useExplorerLink={true} /></div>
            </div>
          </div>
          
          {/* Transaction Details - Unified Component */}
          <GameTransactionHistory
            gameId={gameID}
            gameMakerContract={gameMakerContract}
            gameContract={readOnlyContract}
            gameEvents={riskGameEvents}
            player1={gameState.player1}
            player2={gameState.player2}
          />
        </div>
      </div>

    </div>
  );
};

export default RiskGame;